import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Alert, message } from 'antd';
import { isAxiosError } from 'axios';
import styled from 'styled-components';

import { getPendingTaskBreakdown, getTaskQueueStatus, updateTaskControl } from 'apis/TaskQueueAPI';
import {
  PendingTaskBreakdownType,
  TASK_CONTROL_ACTIONS,
  TaskControlPayloadType,
  TaskQueueStatusType,
} from 'types/tasks';

import TaskQueueHeader from './TaskQueueHeader';
import TaskQueueMetrics from './TaskQueueMetrics';
import TaskQueueTabs from './TaskQueueTabs';

const Page = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
  padding: 10px 30px 30px;
  overflow: hidden;
  background: var(--white);
  box-sizing: border-box;
`;

const TaskQueue = () => {
  const queryClient = useQueryClient();
  const {
    data: status,
    error: statusError,
    isLoading: statusLoading,
    isFetching: statusFetching,
    refetch: refetchStatus,
  } = useQuery<TaskQueueStatusType>({
    queryKey: ['task-queue-status'],
    queryFn: getTaskQueueStatus,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: breakdown,
    error: breakdownError,
    isLoading: breakdownLoading,
  } = useQuery<PendingTaskBreakdownType>({
    queryKey: ['task-queue-pending-breakdown'],
    queryFn: getPendingTaskBreakdown,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const taskControlMutation = useMutation((payload: TaskControlPayloadType) => updateTaskControl(payload), {
    onSuccess: async (_, payload) => {
      message.success(
        payload.action === TASK_CONTROL_ACTIONS.RUN
          ? 'Task run requested'
          : `Task type ${payload.action === TASK_CONTROL_ACTIONS.REVOKE ? 'revoked' : 'restored'}`
      );
      await queryClient.invalidateQueries(['task-queue-status']);
      if (payload.action === TASK_CONTROL_ACTIONS.RUN) {
        await queryClient.invalidateQueries(['task-queue-pending-breakdown']);
      }
    },
    onError: (error: unknown) => {
      const taskError = isAxiosError<{ task_name?: string[] }>(error)
        ? error.response?.data?.task_name?.[0]
        : undefined;
      message.error(typeof taskError === 'string' ? taskError : 'Unable to update the task type');
    },
  });

  return (
    <Page>
      <>
        <TaskQueueHeader
          isFetching={statusFetching}
          lastUpdated={status?.fetched_at}
          onRefresh={() => refetchStatus()}
        />

        {statusError && (
          <Alert
            type="error"
            showIcon
            message="Task queue status is unavailable"
            description="The application could not read the current Huey state. Try refreshing the status."
          />
        )}

        <TaskQueueMetrics status={status} />
        <TaskQueueTabs
          status={status}
          breakdown={breakdown}
          isStatusLoading={statusLoading}
          isBreakdownLoading={breakdownLoading}
          hasBreakdownError={Boolean(breakdownError)}
          isTaskControlUpdating={taskControlMutation.isLoading}
          onChangeTaskRevocation={async (taskName, action) => {
            await taskControlMutation.mutateAsync({ task_name: taskName, action });
          }}
        />
      </>
    </Page>
  );
};

export default TaskQueue;
